/**
 * The home-side inputs of « Les affinités »: the hard rule the household chip
 * names, the pantry foods to use up, and last week's recipes. Read here so the
 * script builder stays pure; the generator's own targets and exclusion are
 * added by useGenerateWeek, which already holds them.
 */
import { useMemo } from 'react';
import { useAppSelector } from '../../redux/hooks';
import type { RootState } from '../../redux/store';
import { selectAllergens, selectCustomAllergens } from '../../redux/userPreferencesSlice';
import { selectActiveInventory } from '../../redux/inventorySlice';
import { selectCurrentWeekStart, selectMealsForWeek } from '../../redux/mealPlanSlice';
import { isRecipeMeal } from '../../redux/mealEntry';
import { isCookedLeftover } from '../../utils/pantryLocation';
import { cleanPantryName } from '../../utils/pantryFoodName';
import { addDays, todayIso } from '../../utils/localDate';
import { firstHouseholdRule, soonestPantryNames, type AffinityContext } from './affinityScript';

const EMPTY: string[] = [];

const pantryDisplayName = (raw: string): string | null => {
  const cleaned = cleanPantryName(raw);
  return cleaned.usable && !cleaned.isStaple ? cleaned.name : null;
};

export function useAffinityContext(): Pick<AffinityContext, 'household' | 'pantry' | 'previousWeekIds'> {
  const allergens = useAppSelector(selectAllergens);
  const customAllergens = useAppSelector(selectCustomAllergens);
  const dislikedIngredients = useAppSelector((s: RootState) => s.userPreferences?.dislikedIngredients ?? EMPTY);
  const dietaryRestrictions = useAppSelector((s: RootState) => s.userPreferences?.dietaryRestrictions ?? EMPTY);
  const inventory = useAppSelector(selectActiveInventory);
  const weekStart = useAppSelector(selectCurrentWeekStart);
  const previousWeek = useAppSelector(selectMealsForWeek(addDays(weekStart, -7)));

  return useMemo(() => ({
    household: firstHouseholdRule({
      allergens,
      customAllergens,
      dislikedIngredients,
      dietaryRestrictions: dietaryRestrictions as any,
    }),
    pantry: soonestPantryNames(
      inventory.filter((i) => !isCookedLeftover(i)),
      todayIso(),
      pantryDisplayName,
    ),
    previousWeekIds: previousWeek.filter(isRecipeMeal).map((m) => m.recipeId),
  }), [allergens, customAllergens, dislikedIngredients, dietaryRestrictions, inventory, previousWeek]);
}
